// FLOW-11 (task 5.2, S11, G17) — o leitor de CEL pequeno do renomear e do colar: acha, numa fórmula,
// os identificadores que leem um passo pelo `id` e os troca. Não é regex sobre o texto (`\bbuscar\b`
// trocaria `x.buscar`, o texto `"buscar"` dentro da fórmula e o `secret('buscar')`): a fórmula é lida
// em tokens, e só conta o identificador que não está dentro de texto entre aspas e não vem depois de
// um `.` (aí ele é campo, e não raiz).
//
// Os passos são percorridos pelo `trocasNosPassos`, que devolve as trocas no TEXTO do YAML
// (posição de início, de fim e o texto novo), cirúrgicas como as outras edições do canvas: o resto do
// escalar, as aspas e os comentários ficam como estavam.
import { isMap, isPair, isScalar, isSeq, Scalar } from 'yaml';

const LETRA = /[A-Za-z_]/;
const LETRA_OU_DIGITO = /[A-Za-z0-9_]/;
const DIGITO = /[0-9]/;
const NUMERO =
  /^(?:0[xX][0-9a-fA-F]+|\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|\.\d+(?:[eE][+-]?\d+)?)[uU]?/;

// O fim do texto entre aspas que começa em `i` (as aspas, simples ou triplas, com o prefixo `r`/`b`
// já pulado). No texto cru (`r'…'`) a barra não escapa nada.
function fimDoTexto(expr, i, cru) {
  const tripla = expr.startsWith(expr[i].repeat(3), i);
  const fecho = tripla ? expr[i].repeat(3) : expr[i];
  let j = i + fecho.length;
  while (j < expr.length) {
    if (!cru && expr[j] === '\\') {
      j += 2;
    } else if (expr.startsWith(fecho, j)) {
      return j + fecho.length;
    } else {
      j += 1;
    }
  }
  return expr.length;
}

// Os identificadores de raiz da fórmula (sem as `{{ }}`): `[{inicio, fim, nome}]`.
export function identificadoresDaCel(expr) {
  const achados = [];
  let i = 0;
  let anterior = '';
  while (i < expr.length) {
    const c = expr[i];
    if (/\s/.test(c)) {
      i += 1;
    } else if (c === '"' || c === "'") {
      i = fimDoTexto(expr, i, false);
      anterior = 'texto';
    } else if (
      /[rRbB]/.test(c) &&
      (/['"]/.test(expr[i + 1] || '') ||
        (/[rRbB]/.test(expr[i + 1] || '') && /['"]/.test(expr[i + 2] || '')))
    ) {
      const prefixo = /['"]/.test(expr[i + 1]) ? 1 : 2;
      const cru = /[rR]/.test(expr.slice(i, i + prefixo));
      i = fimDoTexto(expr, i + prefixo, cru);
      anterior = 'texto';
    } else if (
      DIGITO.test(c) ||
      (c === '.' &&
        DIGITO.test(expr[i + 1] || '') &&
        anterior !== 'nome' &&
        anterior !== ')')
    ) {
      const numero = expr.slice(i).match(NUMERO);
      i += numero ? numero[0].length : 1;
      anterior = 'numero';
    } else if (LETRA.test(c)) {
      let j = i + 1;
      while (j < expr.length && LETRA_OU_DIGITO.test(expr[j])) j += 1;
      if (anterior !== '.') {
        achados.push({ inicio: i, fim: j, nome: expr.slice(i, j) });
      }
      i = j;
      anterior = 'nome';
    } else {
      // `x.?y`, o campo opcional: o `?` depois do ponto continua sendo acesso a campo.
      if (!(c === '?' && anterior === '.')) {
        anterior = c === ')' || c === ']' ? ')' : c;
      }
      i += 1;
    }
  }
  return achados;
}

// A fórmula com os nomes trocados (`mapa`: nome velho → nome novo).
export function trocarNaCel(expr, mapa) {
  let saida = '';
  let ultimo = 0;
  identificadoresDaCel(expr).forEach(({ inicio, fim, nome }) => {
    if (!Object.hasOwn(mapa, nome)) return;
    saida += expr.slice(ultimo, inicio) + mapa[nome];
    ultimo = fim;
  });
  return saida + expr.slice(ultimo);
}

// As posições, no VALOR do texto, dos identificadores a trocar: cada `{{ }}` do texto (o mesmo
// recorte do compilador, `_MOLDE`, não guloso), ou o texto inteiro quando o campo é só fórmula e
// veio sem as chaves. `[{inicio, fim, nome}]`.
const MOLDE = /\{\{([\s\S]*?)\}\}/g;
export function nomesNoTexto(texto, nomes, { inteiro = false } = {}) {
  const achados = [];
  const procurar = (expr, deslocamento) =>
    identificadoresDaCel(expr).forEach(achado => {
      if (nomes.has(achado.nome)) {
        achados.push({
          inicio: achado.inicio + deslocamento,
          fim: achado.fim + deslocamento,
          nome: achado.nome,
        });
      }
    });
  let tem = false;
  [...texto.matchAll(MOLDE)].forEach(m => {
    tem = true;
    procurar(m[1], m.index + 2);
  });
  if (!tem && inteiro) procurar(texto, 0);
  return achados;
}

export function trocarNoTexto(texto, mapa, opcoes) {
  const achados = nomesNoTexto(texto, new Set(Object.keys(mapa)), opcoes);
  let saida = '';
  let ultimo = 0;
  achados.forEach(({ inicio, fim, nome }) => {
    saida += texto.slice(ultimo, inicio) + mapa[nome];
    ultimo = fim;
  });
  return saida + texto.slice(ultimo);
}

// -- do valor do escalar à posição no texto do YAML

// Para cada caractere do valor, a posição dele na fonte; `null` quando a forma do escalar não deixa
// mapear com segurança (texto dobrado em várias linhas), e aí o escalar é reescrito inteiro.
function mapaDoEscalar(texto, no) {
  const [inicio, fim] = no.range;
  const fonte = texto.slice(inicio, fim);
  const posicoes = [];
  if (no.type === Scalar.PLAIN) {
    if (fonte.includes('\n')) return null;
    for (let k = 0; k < fonte.length; k += 1) posicoes.push(inicio + k);
    return posicoes;
  }
  if (no.type === Scalar.QUOTE_SINGLE) {
    if (fonte.includes('\n')) return null;
    for (let k = 1; k < fonte.length - 1; k += 1) {
      posicoes.push(inicio + k);
      if (fonte[k] === "'" && fonte[k + 1] === "'") k += 1;
    }
    return posicoes;
  }
  if (no.type === Scalar.QUOTE_DOUBLE) {
    if (fonte.includes('\n')) return null;
    for (let k = 1; k < fonte.length - 1; k += 1) {
      posicoes.push(inicio + k);
      if (fonte[k] === '\\') {
        const e = fonte[k + 1];
        const tamanho = { x: 2, u: 4, U: 8 }[e];
        if (tamanho) {
          const ponto = parseInt(fonte.slice(k + 2, k + 2 + tamanho), 16);
          if (ponto > 0xffff) posicoes.push(inicio + k);
          k += 1 + tamanho;
        } else {
          k += 1;
        }
      }
    }
    return posicoes;
  }
  if (no.type === Scalar.BLOCK_LITERAL) {
    const quebra = fonte.indexOf('\n');
    if (quebra < 0) return null;
    const linhas = fonte.slice(quebra + 1).split('\n');
    const indicador = fonte.slice(0, quebra).match(/[1-9]/);
    const primeira = linhas.find(l => l.trim());
    const linhaDoPai = texto.slice(
      texto.lastIndexOf('\n', inicio - 1) + 1,
      inicio
    );
    const recuoDoPai = linhaDoPai.match(/^ */)[0].length;
    const recuo = indicador
      ? recuoDoPai + Number(indicador[0])
      : (primeira || '').match(/^ */)[0].length;
    let cursor = inicio + quebra + 1;
    linhas.forEach((linha, n) => {
      for (let k = recuo; k < linha.length; k += 1) posicoes.push(cursor + k);
      if (n < linhas.length - 1) posicoes.push(cursor + linha.length);
      cursor += linha.length + 1;
    });
    return posicoes;
  }
  return null;
}

// As trocas no texto do YAML para um escalar: as dos nomes, cada uma no lugar dela, ou o escalar
// inteiro em aspas duplas quando o mapa não confere.
function trocasDoEscalar(texto, no, mapa, inteiro) {
  if (!isScalar(no) || typeof no.value !== 'string' || !no.range) return [];
  const achados = nomesNoTexto(no.value, new Set(Object.keys(mapa)), {
    inteiro,
  });
  if (!achados.length) return [];
  const posicoes = mapaDoEscalar(texto, no);
  const conferem =
    posicoes &&
    achados.every(
      ({ inicio, fim, nome }) =>
        posicoes[inicio] !== undefined &&
        texto.slice(posicoes[inicio], posicoes[inicio] + (fim - inicio)) ===
          nome &&
        posicoes[fim - 1] === posicoes[inicio] + (fim - inicio) - 1
    );
  if (conferem) {
    return achados.map(({ inicio, fim, nome }) => ({
      inicio: posicoes[inicio],
      fim: posicoes[inicio] + (fim - inicio),
      texto: mapa[nome],
    }));
  }
  return [
    {
      inicio: no.range[0],
      fim: no.range[1],
      texto: JSON.stringify(trocarNoTexto(no.value, mapa, { inteiro })),
    },
  ];
}

// Os campos que são só fórmula, mesmo sem as chaves (G17): o compilador os lê inteiros. Conferidos
// contra o schema em teste (`$defs/condicao`, `$defs/itens` e o "sozinha em `{{ }}`").
export const CAMPOS_SO_FORMULA = ['when', 'where', 'until', 'items'];
// As listas de passos dentro de um passo, que o percurso desce como passos.
const LISTAS = ['then', 'else', 'default', 'steps'];
// O que não é fórmula: o nome do passo, a nota, o tipo e, por tipo, o texto que é de outra língua
// (o JavaScript do `code` e o HTML do `document.render`, cujas `{{ }}` leem o `data` dele).
const FORA = ['id', 'description', 'note', 'type', 'disabled'];
const FORA_POR_TIPO = {
  code: ['code'],
  'document.render': ['template'],
  tool: ['name'],
};

const valorDoPar = (mapa, chave) =>
  mapa.items.find(p => isScalar(p.key) && String(p.key.value) === chave)?.value;

// As trocas de nomes em todos os passos de uma lista (e no que está dentro deles), no texto.
export function trocasNosPassos(texto, lista, mapa) {
  const trocas = [];
  const generico = (no, inteiro = false) => {
    if (isScalar(no)) trocas.push(...trocasDoEscalar(texto, no, mapa, inteiro));
    else if (isMap(no)) no.items.forEach(p => isPair(p) && generico(p.value));
    else if (isSeq(no)) no.items.forEach(item => generico(item));
  };
  function passo(mapaDoPasso) {
    const passos = seq =>
      isSeq(seq) && seq.items.forEach(item => isMap(item) && passo(item));
    const tipo = String(valorDoPar(mapaDoPasso, 'type')?.value ?? '');
    const fora = [...FORA, ...(FORA_POR_TIPO[tipo] || [])];
    mapaDoPasso.items.forEach(par => {
      const chave = isScalar(par.key) ? String(par.key.value) : null;
      const valor = par.value;
      if (!chave || fora.includes(chave)) return;
      if (LISTAS.includes(chave) && isSeq(valor)) {
        passos(valor);
      } else if (chave === 'cases' && isMap(valor)) {
        valor.items.forEach(caso => passos(caso.value));
      } else if (chave === 'branches' && tipo === 'branches' && isSeq(valor)) {
        valor.items.forEach(ramo => {
          if (!isMap(ramo)) return;
          ramo.items.forEach(p => {
            const nome = String(p.key?.value);
            if (nome === 'steps') passos(p.value);
            else if (nome === 'when') generico(p.value, true);
            else if (nome !== 'name') generico(p.value);
          });
        });
      } else if (chave === 'on_error' && isMap(valor)) {
        valor.items.forEach(p =>
          String(p.key?.value) === 'steps' ? passos(p.value) : generico(p.value)
        );
      } else if (chave === 'by' && isSeq(valor)) {
        valor.items.forEach(criterio => {
          if (!isMap(criterio)) return;
          criterio.items.forEach(p =>
            generico(p.value, String(p.key?.value) === 'field')
          );
        });
      } else {
        generico(valor, CAMPOS_SO_FORMULA.includes(chave));
      }
    });
  }
  if (isSeq(lista)) lista.items.forEach(item => isMap(item) && passo(item));
  return trocas;
}

// As trocas aplicadas ao texto, da última para a primeira (as posições de antes continuam valendo).
export function aplicarTrocas(texto, trocas) {
  return [...trocas]
    .sort((a, b) => b.inicio - a.inicio)
    .reduce(
      (atual, { inicio, fim, texto: novo }) =>
        atual.slice(0, inicio) + novo + atual.slice(fim),
      texto
    );
}
